import { useEffect, useMemo, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { KPICard } from "@/components/KPICard";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BadgeIndianRupee, TrendingUp, UserPlus, Repeat2, Sparkles,
  Clock, ClipboardCheck, ClipboardX, Wallet, Target as TargetIcon,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { useAuth } from "@/lib/auth";
import { formatCompactINR, formatINR, formatNumber, formatPercent, initials, ROLE_LABEL } from "@/lib/format";
import { periodPresets, presetToPeriod } from "@/lib/periods";
import { toast } from "sonner";

// Stable module-scope references for chart props (avoids new-object-per-render).
const CHART_MARGIN = { top: 8, right: 12, left: -12, bottom: 0 };
const CHART_TOOLTIP_STYLE = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
};
const CHART_DOT_SMALL = { r: 2 };
const CHART_DOT_ACTIVE = { r: 5 };
const CHART_BAR_RADIUS = [6, 6, 0, 0];

export default function PerformancePage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const initial = useMemo(() => presetToPeriod(periodPresets().this_month), []);
  const [period, setPeriod] = useState(initial);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users");
      setUsers(data);
      // Auto-select: self for exec, first exec for others
      if (user.role === "sales_executive") setSelected(user.id);
      else {
        const firstExec = data.find((u) => u.role === "sales_executive");
        setSelected(firstExec?.id || data[0]?.id || null);
      }
    })().catch((e) => toast.error(extractError(e)));
  }, [user.role, user.id]);

  const loadPerformance = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/user-performance/${selected}`, {
        params: { start_date: period.start_date, end_date: period.end_date },
      });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selected, period.start_date, period.end_date]);

  useEffect(() => { loadPerformance(); }, [loadPerformance]);

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter(
      (u) => u.name.toLowerCase().includes(s) ||
             u.employee_id.toLowerCase().includes(s) ||
             u.email.toLowerCase().includes(s),
    );
  }, [users, q]);

  const trendData = (data?.trend || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
    depositL: +(d.deposit / 100000).toFixed(2),
  }));

  return (
    <div className="space-y-6" data-testid="performance-page">
      <SectionHeader
        title="Individual Performance"
        description="Deep-dive into any employee's targets, achievements, clients, and submissions."
        actions={<PeriodPicker value={period} onChange={setPeriod} testid="perf-period" />}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Employee selector */}
        <Card className="rounded-xl border border-border bg-card p-3 shadow-sm">
          <div className="p-2">
            <Input
              data-testid="perf-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or employee ID…"
              className="mb-2"
              disabled={user.role === "sales_executive"}
            />
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {filtered.map((u) => (
              <button
                key={u.id}
                type="button"
                data-testid={`perf-user-${u.employee_id}`}
                onClick={() => setSelected(u.id)}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${
                  selected === u.id ? "bg-primary/10" : "hover:bg-muted"
                }`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-muted text-[10px] font-semibold">{initials(u.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{u.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground tabular-nums">
                    {u.employee_id} · {ROLE_LABEL[u.role]}
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="p-4 text-center text-xs text-muted-foreground">No employees match.</div>
            )}
          </div>
        </Card>

        {/* Metrics */}
        <div className="space-y-6">
          {loading || !data ? (
            <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
              {loading ? "Loading…" : "Select an employee to view performance."}
            </div>
          ) : (
            <>
              <Card data-testid="perf-user-card" className="rounded-xl border border-border bg-card p-5 shadow-sm">
                <div className="flex flex-wrap items-center gap-4">
                  <Avatar className="h-14 w-14">
                    <AvatarFallback className="bg-primary/10 text-lg font-semibold text-primary">{initials(data.user.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-display text-xl font-semibold tracking-tight">{data.user.name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      {data.user.employee_id} · {ROLE_LABEL[data.user.role]} · {data.user.email}
                    </div>
                  </div>
                </div>
              </Card>

              <div>
                <div className="label-caps mb-3">Deposit Performance (Weekly)</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <KPICard testid="perf-week-target" label="Weekly Target" value={formatCompactINR(data.targets.weekly_deposit)} icon={TargetIcon} />
                  <KPICard testid="perf-week-achieved" label="Achieved" value={formatCompactINR(data.weekly.deposit)} icon={BadgeIndianRupee} tone="primary" />
                  <KPICard testid="perf-week-remaining" label="Remaining" value={formatCompactINR(data.weekly.remaining_deposit)} icon={Wallet} tone="warning" />
                  <KPICard testid="perf-week-pct" label="Achievement %" value={formatPercent(data.weekly.deposit_pct)} progress={data.weekly.deposit_pct} icon={TrendingUp} tone={data.weekly.deposit_pct >= 100 ? "success" : "default"} />
                </div>
              </div>

              <div>
                <div className="label-caps mb-3">Deposit Performance (Monthly)</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <KPICard testid="perf-month-target" label="Monthly Target" value={formatCompactINR(data.targets.monthly_deposit)} icon={TargetIcon} />
                  <KPICard testid="perf-month-achieved" label="Achieved" value={formatCompactINR(data.monthly.deposit)} icon={BadgeIndianRupee} tone="primary" />
                  <KPICard testid="perf-month-remaining" label="Remaining" value={formatCompactINR(data.monthly.remaining_deposit)} icon={Wallet} tone="warning" />
                  <KPICard testid="perf-month-pct" label="Achievement %" value={formatPercent(data.monthly.deposit_pct)} progress={data.monthly.deposit_pct} icon={TrendingUp} tone={data.monthly.deposit_pct >= 100 ? "success" : "default"} />
                </div>
              </div>

              <div>
                <div className="label-caps mb-3">Account Opening Performance</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <KPICard testid="perf-wa-target" label="Weekly Target" value={formatNumber(data.targets.weekly_accounts)} icon={TargetIcon} hint={`Remaining: ${data.weekly.remaining_accounts}`} />
                  <KPICard testid="perf-wa-achieved" label="Weekly Opened" value={formatNumber(data.weekly.accounts)} icon={UserPlus} tone="primary" progress={data.weekly.accounts_pct} />
                  <KPICard testid="perf-ma-target" label="Monthly Target" value={formatNumber(data.targets.monthly_accounts)} icon={TargetIcon} hint={`Remaining: ${data.monthly.remaining_accounts}`} />
                  <KPICard testid="perf-ma-achieved" label="Monthly Opened" value={formatNumber(data.monthly.accounts)} icon={UserPlus} tone="primary" progress={data.monthly.accounts_pct} />
                </div>
              </div>

              <div>
                <div className="label-caps mb-3">Client Performance ({period.label})</div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <KPICard testid="perf-new-clients" label="New Clients" value={formatNumber(data.clients.new)} icon={UserPlus} />
                  <KPICard testid="perf-existing-clients" label="Existing Clients" value={formatNumber(data.clients.existing)} icon={Repeat2} />
                  <KPICard testid="perf-week-active" label="Weekly Active" value={formatNumber(data.clients.weekly_active)} icon={Sparkles} tone="success" />
                  <KPICard testid="perf-month-active" label="Monthly Active" value={formatNumber(data.clients.monthly_active)} icon={Sparkles} tone="success" />
                </div>
              </div>

              <div>
                <div className="label-caps mb-3">Submission Status ({period.label})</div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <KPICard testid="perf-pending" label="Pending" value={formatNumber(data.submissions_status.pending.n)} icon={Clock} tone="warning" hint={formatINR(data.submissions_status.pending.amt)} />
                  <KPICard testid="perf-approved" label="Approved" value={formatNumber(data.submissions_status.approved.n)} icon={ClipboardCheck} tone="success" hint={formatINR(data.submissions_status.approved.amt)} />
                  <KPICard testid="perf-rejected" label="Rejected" value={formatNumber(data.submissions_status.rejected.n)} icon={ClipboardX} tone="danger" hint={formatINR(data.submissions_status.rejected.amt)} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card data-testid="perf-chart-deposit" className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="label-caps mb-2">Deposit Trend</div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={CHART_MARGIN}>
                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`₹${v} L`, "Deposit"]} />
                        <Line type="monotone" dataKey="depositL" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={CHART_DOT_SMALL} activeDot={CHART_DOT_ACTIVE} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
                <Card data-testid="perf-chart-accounts" className="rounded-xl border border-border bg-card p-5 shadow-sm">
                  <div className="label-caps mb-2">Account Opening Trend</div>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trendData} margin={CHART_MARGIN}>
                        <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                        <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Bar dataKey="accounts" fill="hsl(var(--chart-2))" radius={CHART_BAR_RADIUS} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
